import { Box, Flex, Text } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { TrendFrequency } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { dateFormat } from '@fullstory/util/date';
import { shortNumber } from '@fullstory/util/fmt';
import {
  AbstractHoverZone,
  BottomAxis,
  Chart as VisualizationChart,
  ChartSpacing,
  GridLines,
  GuidelineGroup,
  HoverLine,
  LeftAxis,
  Line,
  Point,
  useLeftAxis,
  useVisualizationColors,
} from '@fullstory/visualizations';
import { extent, line, max, min, scaleLinear, scaleTime, ticks, timeDay } from 'd3';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface ChartProps {
  volume: Point[];
  frequency: TrendFrequency;
  height?: number;
  sparkline?: boolean;
}

const SPARKLINE_CHART_WIDTH = 348;

export const ActivityMonitoringTrendsChart: React.FC<ChartProps> = ({
  volume,
  frequency,
  height = 300,
  sparkline = false,
}) => {
  const spacing = useMemo(() => {
    if (sparkline) {
      return { top: 0, right: 0, bottom: 2, left: 0 };
    }
    return { top: 8, right: 38, bottom: 50, left: 50 };
  }, [sparkline]);

  return (
    <VisualizationChart
      data-component="ActivityMonitoringTrendsChart"
      width={sparkline ? SPARKLINE_CHART_WIDTH : undefined}
      height={height}
      spacing={spacing}
    >
      {({ width, height, overlay }) => (
        <InnerChart
          width={width}
          height={height}
          overlay={overlay}
          spacing={spacing}
          volume={volume}
          frequency={frequency}
          sparkline={sparkline}
        />
      )}
    </VisualizationChart>
  );
};

// uses a constant 0-100% scale
const MIN_Y = 0;
const MAX_Y = 1;

const InnerChart: React.FC<
  {
    width: number;
    height: number;
    overlay: HTMLDivElement | null;
    spacing: ChartSpacing;
  } & ChartProps
> = ({ width, height, overlay, volume, frequency, spacing, sparkline }) => {
  const [tooltipData, setTooltipData] = useState<Point[] | null>(null);
  const [tooltipX, setTooltipX] = useState<number | undefined>(undefined);

  const domain = extent(volume, d => d.endDate);

  const xScale = scaleTime()
    .domain(domain[0] !== undefined ? domain : [0, 0])
    .range([0, width]);

  const maxYValue = Math.max(max(volume, d => d.value) ?? 0, 0);
  const minYValue = min(volume, d => d.value) ?? 0;

  // normalizing our max/min range to nice round numbers.
  // this makes our chart have a label for every horizontal line.
  //
  // for example, if our min is 6394 and our max is 8932,
  // our range should be 6k - 9k.
  // This depends on the decimal difference - if the range was 6394
  // to 6891, we would want a range of 6300 to 6900. If 6394
  // to 6436, the range would be 6390 to 6440.

  const difference = maxYValue - minYValue;
  const digits = difference.toString().length;
  const digitsDecimal = 10 ** (digits - 1);
  const maxY = maxYValue - (maxYValue % digitsDecimal) + digitsDecimal;
  const minY = minYValue + (digitsDecimal - (minYValue % digitsDecimal)) - digitsDecimal;

  const yScale = useMemo(
    () => scaleLinear().domain([minY, maxY]).range([height, 0]),
    [minY, maxY, height],
  );

  const yTicks = useMemo(() => ticks(minY, maxY, 5), [minY, maxY]);

  // responsively decide number of ticks on chart
  let tickDivisor = 4;
  if (width < 600) {
    tickDivisor = 6;
  }
  if (width < 400) {
    tickDivisor = 12;
  }

  // one tick per day if daily, otherwise we are hourly
  const xTicks =
    frequency === TrendFrequency.TREND_FREQUENCY_DAILY
      ? xScale.ticks(timeDay)
      : xScale.ticks(Math.floor(volume.length / tickDivisor));

  // we've already normalized to decimal values, so should get clean numbers when dividing by 1000
  const yValues = useLeftAxis(yScale, yTicks);

  const currLine = useMemo(
    () =>
      line<Point>()
        .x(d => xScale(d.endDate))
        .y(d => yScale(d.value)),
    [xScale, yScale],
  );

  const VISUALIZATION_COLORS = useVisualizationColors();

  if (sparkline) {
    return (
      <>
        <GuidelineGroup>
          <line
            x2={width}
            stroke="black"
            strokeWidth={1}
            transform={`translate(0,${height})`}
            strokeDasharray="5,5"
          />
        </GuidelineGroup>
        <Line<Point> line={currLine} data={volume} color={VISUALIZATION_COLORS[0]} />
      </>
    );
  }

  return (
    <>
      <AbstractHoverZone<Point>
        overlay={overlay}
        dataPoints={volume}
        dataToX={d => d.endDate.getTime()}
        dataToY={d => d.value}
        xScale={xScale}
        getInvertedXScaleValue={x => xScale.invert(x).getTime()}
        yScale={yScale}
        tooltipX={tooltipX}
        setTooltipX={setTooltipX}
        tooltipData={tooltipData}
        setTooltipData={setTooltipData}
      >
        {data => {
          const d = data[0];
          if (!d) return null;
          return (
            <Box padding={8}>
              <Flex column alignItems="flex-start">
                {d.endDate && (
                  <Text color="default-inverse" appearance="bodySmall">
                    {dateFormat(d.endDate, { format: 'abbreviated' })}
                  </Text>
                )}
                <Text color="default-inverse" appearance="bodySmall">
                  {shortNumber(d.value)} <FormattedMessage defaultMessage="events" id="yi/uLq" />
                </Text>
              </Flex>
            </Box>
          );
        }}
      </AbstractHoverZone>
      <BottomAxis<Date>
        width={width}
        height={height}
        xScale={xScale}
        xTicks={xTicks}
        spacing={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 16,
        }}
        rotate
        ruler
      >
        {(xValue: Date) => xTimeFormat(xValue, frequency)}
      </BottomAxis>
      <LeftAxis height={height} spacing={spacing} yValues={yValues} />
      <GridLines
        width={width}
        height={height}
        xTicks={xTicks}
        yTicks={yTicks}
        xScale={xScale}
        yScale={yScale}
      />
      {tooltipData && (
        <HoverLine x={tooltipX} yStart={MIN_Y ? yScale(MAX_Y) : undefined} yEnd={yScale(0)} />
      )}
      <Line<Point> line={currLine} data={volume} color={VISUALIZATION_COLORS[0]} />
    </>
  );
};

const xTimeFormat = (date: Date, frequency: TrendFrequency): string => {
  if (
    frequency === TrendFrequency.TREND_FREQUENCY_HOURLY ||
    frequency === TrendFrequency.TREND_FREQUENCY_MINUTELY
  ) {
    return intl.formatTime(date, { timeStyle: 'short' });
  } else if (
    frequency === TrendFrequency.TREND_FREQUENCY_WEEKLY ||
    frequency === TrendFrequency.TREND_FREQUENCY_MONTHLY
  ) {
    return intl.formatDate(date, { month: 'numeric', day: '2-digit', year: '2-digit' });
  }
  return intl.formatDate(date, { day: '2-digit', month: 'numeric' });
};
